// @flow
import { type OperationComponent } from "@apollo/client";
import { get, first } from "lodash";
import { format } from "date-fns";
import { formatPastRaceDate } from "@tvg/formatter/dates";
import type { WroWagerGroup, WroWager } from "@tvg/api/wro/types";
import type { Props } from "../types";

export const formatBets = (
  groupWagers: WroWagerGroup[],
  groupWagerValue: string
) => {
  const wagers =
    first(groupWagers.filter((wager) => wager.value === groupWagerValue))
      ?.wagers || [];
  return wagers
    .filter((wager) => get(wager, "betStatus.code") !== "C")
    .map((wager) => ({
      id: wager.serialNumber,
      raceTypeCode: wager.raceTypeAbbreviation,
      betTotal: wager.betTotal,
      wagerAmount: wager.wagerAmount,
      wagerType: wager.wagerType,
      selections: wager.selections.selection.map((selection) =>
        selection.runners.map((runner) => runner.number.toString())
      ),
      isKey: false,
      isLeg: wager.wagerType.id < 400 && wager.wagerType.id > 300,
      isCancelled: wager.betStatus.code === "C",
      isLost: wager.betStatus.code === "L",
      isRefunded: wager.betStatus.code === "R",
      totalWinAmount:
        wager.betStatus.code === "W" ? wager.winningsAmount : undefined
    }));
};

export const formatGroupWagers = (
  groupWagers: WroWagerGroup[],
  groupWagerValue: string
): Array<{ value: string, wagers: WroWager[] }> =>
  groupWagers
    .filter((wager) => wager.value === groupWagerValue)
    .map((groupWager) => ({
      value: groupWager.value.replace(/#/gi, "|"),
      wagers: groupWager.wagers.filter(
        (wager) => get(wager, "betStatus.code") !== "C"
      )
    }));

export const getVariables = (props: Props) => {
  const startDate = new Date();

  return {
    trackCode: props.trackAbbr,
    raceNumber: props.raceNumber,
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(startDate, "yyyy-MM-dd"),
    accountId: parseInt(props.accountNumber, 10)
  };
};

export const getGroupWagerValue = (props: Props) => {
  const raceId = get(props, "currentRace.id", "").replace(/-/gi, "#");
  const postDate = formatPastRaceDate(get(props, "currentRace.postTime"));
  return `${postDate}#${raceId}`;
};

export default {
  skip: (props: Props) => !props.accountNumber || !props.behgToggle,
  options: (props: Props) => {
    const poll = get(props, "hasWebSockets", false)
      ? {}
      : { pollInterval: 30000 };

    return {
      ...poll,
      fetchPolicy: "cache-and-network",
      client: props.behgClient,
      ssr: false,
      variables: getVariables(props)
    };
  },
  props: (result: OperationComponent<Response>) => {
    return {
      userRaceBets: formatBets(
        get(result, "data.wagerHistory.groupWagers", []),
        getGroupWagerValue(result.ownProps)
      ),
      queryGroupWagers: formatGroupWagers(
        get(result, "data.wagerHistory.groupWagers", []),
        getGroupWagerValue(result.ownProps)
      ),
      isLoading: result.data.loading,
      subscribeToMore:
        get(result, "ownProps.hasWebSockets", false) && !result.data.loading
          ? result.data.subscribeToMore
          : null
    };
  }
};
